/**
 * Où en est le joueur dans la suite d'accords d'une unité.
 *
 * Pur : ni React, ni transport, ni DOM. Le hook qui l'alimente est trois lignes
 * au-dessus ; la règle, elle, est ici, où elle se teste.
 *
 * ## Une seule question, et deux façons d'y répondre
 *
 * « Quel accord est sous les mains ». En rythme, c'est le transport qui le dit,
 * par `chordIndexAtTick`. À l'arrêt, c'est le transport aussi : l'écran **est**
 * le roll dit autrement, et une seule tête de lecture les tient tous les deux —
 * prendre un accord avance le transport, déplacer le transport déplace l'écran.
 * Deux compteurs séparés donneraient deux réponses à « où en suis-je dans le
 * morceau », et le joueur verrait le roll et l'écran se contredire dès le
 * premier clic dans la minimap.
 *
 * Ce module a porté davantage : l'accord **suivant**, le droit du bandeau de
 * l'annoncer, et le remplissage de la cellule courante du ruban. Les trois ne
 * servaient qu'au décor à couloir ouvert, celui des niveaux 5 à 9, qui ne sont
 * pas opérationnels et dont l'écran est parti. Voir `scene.ts`.
 */

/**
 * Une fenêtre d'accord, réduite à ce que l'écran en lit.
 *
 * Structurel et pas `AnalysisChord`, pour une raison précise : le curseur
 * travaille sur les fenêtres **rognées à l'étendue qu'on joue**, dont les
 * bornes ne sont pas forcément celles de l'analyse. Un `AnalysisChord` dont on
 * aurait réécrit `tick` et `endTick` serait un `AnalysisChord` qui ment ; ceci
 * est un autre type, qui dit ce qu'il est.
 */
export type HarmonyWindow = {
  readonly id: string;
  readonly symbol: string;
  readonly romanNumeral: string;
  /** De quoi épeler l'accord en toutes lettres. */
  readonly root: string;
  readonly quality: string;
  readonly tick: number;
  readonly endTick: number;
};

export type HarmonyCursorInput =
  | {
      readonly regime: "timed";
      /** Les accords de l'unité, dans l'ordre, sans trou. */
      readonly chords: readonly HarmonyWindow[];
      /** La position du transport, en ticks. */
      readonly tick: number;
    }
  | {
      readonly regime: "free";
      readonly chords: readonly HarmonyWindow[];
      /** L'accord sur lequel le joueur travaille. */
      readonly index: number;
    };

export type HarmonyCursor = {
  /** Rang de l'accord courant, ou `-1` avant le premier. */
  readonly index: number;
  readonly current: HarmonyWindow | null;
};

const EMPTY: HarmonyCursor = { index: -1, current: null };

/**
 * Le chiffrage sans la basse écrite : `Bm/D` devient `Bm`.
 *
 * Sert là où le niveau ne joue pas la basse du morceau. Découpage sur la barre
 * et non reconstruction depuis la fondamentale et la qualité : c'est un
 * chiffrage, et le rebâtir demanderait de réécrire la convention de notation
 * que l'analyseur applique déjà, avec ses altérations et ses extensions.
 * Retirer la basse est la seule chose à faire.
 */
export function withoutWrittenBass(symbol: string): string {
  const slash = symbol.indexOf("/");

  return slash === -1 ? symbol : symbol.slice(0, slash);
}

/**
 * De combien un tick peut manquer une borne sans qu'on le lui compte.
 *
 * La tête de lecture ne stocke pas des ticks, elle stocke un temps : demander
 * le tick 5760 y revient à 5759,999999999945, et huit débuts de fenêtre sur
 * dix du *Lac des cygnes* atterrissent ainsi **sous** leur cible. Mesuré, pas
 * supposé, et l'événement de saut porte la même valeur dérivée que la lecture
 * directe, donc il n'y a pas de côté propre où se brancher.
 *
 * Sans tolérance, une comparaison stricte range ce tick dans la fenêtre
 * **précédente**. L'écran remontrait alors l'accord qu'on vient de confirmer,
 * la garde de confirmation refuserait de le reconfirmer, et plus rien
 * n'avancerait : la barre finie, l'accord joué, et aucun effet. C'était
 * intermittent parce que quelques bornes tombent juste.
 *
 * Un demi-tick, parce que c'est la moitié de la plus petite distance qui
 * sépare deux bornes : rien ne peut basculer d'une fenêtre à sa voisine, et à
 * 480 pulsations par noire pour 100 à la noire cela vaut six dixièmes de
 * milliseconde, soit très en deçà de ce qu'une oreille ou une main distingue.
 */
export const TICK_EPSILON = 0.5;

/**
 * L'accord en cours à ce tick, ou le dernier commencé. `-1` avant le premier.
 *
 * Exporté parce que le régime libre lit sa position là-dessus :
 * c'est la même règle que le régime en rythme, appliquée à une tête de lecture
 * qui saute au lieu de courir, et l'écrire deux fois les ferait diverger le
 * jour où l'une des deux gagnerait une borne.
 *
 * Recherche linéaire assumée : une unité tient une dizaine de fenêtres
 * d'accord, et une dichotomie sur dix éléments est une complication qui se
 * relit moins bien qu'elle ne calcule vite.
 */
export function chordIndexAtTick(chords: readonly HarmonyWindow[], tick: number): number {
  let found = -1;

  for (let i = 0; i < chords.length; i += 1) {
    if (chords[i].tick > tick + TICK_EPSILON) {
      break;
    }

    found = i;
  }

  return found;
}

/**
 * Jusqu'où cette fenêtre emmène la tête de lecture.
 *
 * ## Les fenêtres ne se touchent pas
 *
 * On l'a longtemps cru, et c'était faux : l'analyse termine un accord là où
 * ses notes s'arrêtent, pas là où le suivant commence. Le *Menuet en sol
 * mineur* a quatorze trous sur cinquante-trois raccords, d'un temps à cinq
 * temps chacun. Le niveau en creuse d'autres pour son propre compte : ce qu'il
 * ne fait pas jouer n'est pas montré, donc une fenêtre sans note de la
 * réduction disparaît de la suite en y laissant sa durée. Voir
 * `playableChords`.
 *
 * ## Ce que le trou faisait
 *
 * Le dernier segment d'une fenêtre s'arrêtait à la fin de **celle-ci**. La
 * tête de lecture arrivait donc au bord du trou et s'y posait, dans un
 * intervalle qui n'appartient à aucune fenêtre : `chordIndexAtTick` y rend
 * encore celle qu'on vient de faire, dont toutes les portes sont franchies.
 * Plus une seule ne pouvait s'ouvrir, et l'écran restait là, juste avant le
 * silence, à attendre une frappe qu'il n'attendait pas.
 *
 * ## Le silence appartient à ce qu'on vient de jouer
 *
 * La fenêtre emmène donc jusqu'à la frappe suivante, trou compris, et le
 * morceau le parcourt à sa vitesse : on entend les notes s'éteindre, puis le
 * silence écrit, puis l'accord suivant arrive à l'heure. C'est la même règle
 * que la levée, prise par l'autre bout — celle-ci joue le silence qui précède
 * la première frappe d'une fenêtre, celle-là le silence qui suit la dernière.
 *
 * La dernière fenêtre du niveau n'a rien après elle : elle s'arrête chez
 * elle, et c'est le niveau qui se termine.
 *
 * Rien de tout cela ne change le **dessin** : la frise garde les bornes de
 * l'analyse, sans quoi la dernière note se prolongerait sur le silence et
 * l'écran écrirait une tenue que le morceau n'a pas.
 */
export function windowReach(chords: readonly HarmonyWindow[], index: number): number {
  const window = chords[index];

  if (window === undefined) {
    return 0;
  }

  return chords[index + 1]?.tick ?? window.endTick;
}

/** Une unité du plan, réduite à ce que la mesure en lit. */
export type MeasuredUnit = {
  readonly id: string;
  readonly startTick: number;
};

/**
 * L'unité qui contient ce tick, ou aucune.
 *
 * La dernière qui a commencé, et non celle dont les bornes encadrent le tick :
 * le plan a des trous d'unités — `unitsWithSomethingToPlay` retire celles que
 * ce niveau ne dote pas — et une fenêtre qui tombe dans un trou appartient,
 * pour la session, à l'unité qui la précède. Même tolérance que
 * `chordIndexAtTick`, pour la même raison.
 */
export function unitAtTick(units: readonly MeasuredUnit[], tick: number | null): string | undefined {
  if (tick === null) {
    return undefined;
  }

  const at = unitIndexAtTick(units, tick);

  return at < 0 ? undefined : units[at].id;
}

/**
 * Les unités qu'une fenêtre finit d'enchaîner quand elle a sonné, dans l'ordre.
 *
 * Celle où la fenêtre commence, puis toutes celles qui suivent jusqu'à celle où
 * commence la fenêtre d'après, exclue ; jusqu'à la dernière quand il n'y a plus
 * de fenêtre après. Vide quand la suivante commence dans la même unité.
 *
 * ## Pourquoi plus d'une
 *
 * Les fenêtres ont été rognées aux unités : chaque unité avait alors la sienne
 * qui commençait chez elle, et comparer l'unité d'une fenêtre à celle de la
 * suivante suffisait. Elles se taillent désormais sur le morceau entier — un
 * accord tenu par-dessus une frontière est une seule fenêtre — et une unité
 * que cet accord couvre de bout en bout n'a plus aucune fenêtre qui commence
 * chez elle. La comparaison ne l'aurait jamais comptée, alors que le joueur
 * l'a traversée. Voir `playableChords`.
 */
export function unitsChained(
  units: readonly MeasuredUnit[],
  tick: number,
  nextTick: number | null,
): string[] {
  const from = unitIndexAtTick(units, tick);

  if (from < 0) {
    return [];
  }

  const until = nextTick === null ? units.length : unitIndexAtTick(units, nextTick);

  return units.slice(from, Math.max(until, from)).map((unit) => unit.id);
}

function unitIndexAtTick(units: readonly MeasuredUnit[], tick: number): number {
  let found = -1;

  for (let i = 0; i < units.length; i += 1) {
    if (units[i].startTick > tick + TICK_EPSILON) {
      break;
    }

    found = i;
  }

  return found;
}

export function harmonyCursor(input: HarmonyCursorInput): HarmonyCursor {
  const { chords } = input;

  if (chords.length === 0) {
    return EMPTY;
  }

  if (input.regime === "free") {
    const index = Math.min(Math.max(input.index, 0), chords.length - 1);

    return { index, current: chords[index] };
  }

  const index = chordIndexAtTick(chords, input.tick);

  return index < 0 ? EMPTY : { index, current: chords[index] };
}
